import * as Sentry from '@sentry/nextjs';
import { memo, useEffect } from 'react';

import { ABSmartly } from '@catawiki/ab-test';

import * as Experiments from '@/shared/constants/experiments';

const sellerUiExperiments: string[] = Object.values(Experiments);

interface SentryContextProviderProps {
  userId?: number | string;
}

function SentryContextProvider({ userId }: SentryContextProviderProps) {
  const { context: absmartlyContext } = ABSmartly.useABSmartly();

  useEffect(() => {
    async function setSentryMetadata() {
      if (userId) {
        Sentry.setUser({ id: String(userId) });
      } else {
        Sentry.setUser(null);
      }

      await absmartlyContext
        .ready()
        .then(() => {
          const flagsIntegration =
            Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>(
              'FeatureFlags'
            );

          if (!flagsIntegration) {
            return;
          }

          const experiments = absmartlyContext.experiments() || [];

          experiments.forEach((experimentName) => {
            if (sellerUiExperiments.includes(experimentName)) {
              const variant = absmartlyContext.peek(experimentName);
              flagsIntegration.addFeatureFlag(experimentName, String(variant));
            }
          });
        })
        .catch(() => {
          // Intentionally ignoring ABSmartly context errors to avoid blocking Sentry setup.
        });
    }

    setSentryMetadata().catch(() => {
      // Intentionally ignoring errors to avoid blocking Sentry setup.
    });
  }, [absmartlyContext, userId]);

  return null;
}

export const SentryProvider = memo(SentryContextProvider);
